import { create } from 'zustand';
import {
  Edge,
  Node,
  Connection,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  NodeChange,
  EdgeChange,
} from 'reactflow';
import { api } from '@/lib/api';

export type ColumnType =
  | 'varchar'
  | 'text'
  | 'integer'
  | 'bigint'
  | 'float'
  | 'double'
  | 'decimal'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'timestamp'
  | 'time'
  | 'uuid'
  | 'json'
  | 'jsonb'
  | 'enum'
  | 'array';

export interface Column {
  id: string;
  name: string;
  type: ColumnType;
  length?: number;
  precision?: number;
  scale?: number;
  nullable: boolean;
  primaryKey: boolean;
  unique: boolean;
  autoIncrement?: boolean;
  defaultValue?: string;
  comment?: string;
}

export interface TableData {
  name: string;
  columns: Column[];
  indexes?: Index[];
  comment?: string;
}

export interface Index {
  id: string;
  name: string;
  columns: string[];
  unique: boolean;
}

export interface TableNode extends Node {
  data: TableData;
  type?: 'tableNode';
}

export interface SavedSchema {
  id: string;
  name: string;
  description?: string;
  databaseId?: string;
  nodes: TableNode[];
  edges: Edge[];
  createdAt: Date;
  updatedAt: Date;
}

interface HistoryState {
  nodes: TableNode[];
  edges: Edge[];
}

export interface SchemaDesignerState {
  // Schema data
  nodes: TableNode[];
  edges: Edge[];
  currentDatabaseId?: string;

  // Saved schemas
  savedSchemas: SavedSchema[];
  currentSchemaId?: string;

  // UI state
  isExecuting: boolean;
  isSaving: boolean;
  isLoading: boolean;
  lastSaveTime?: Date;
  syncStatus?: 'idle' | 'saving' | 'loading' | 'error';
  syncError?: string;
  _autoSaveTimer?: NodeJS.Timeout;

  // Undo/Redo
  history: HistoryState[];
  historyIndex: number;
  canUndo: boolean;
  canRedo: boolean;

  // Internal helpers
  _autoSaveToCloud: () => Promise<void>;

  // Node/Edge operations
  setNodes: (nodes: TableNode[]) => void;
  setEdges: (edges: Edge[]) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;

  // Table operations
  addTable: (table: TableData) => void;
  updateTable: (id: string, data: Partial<TableData>) => void;
  removeTable: (id: string) => void;
  duplicateTable: (id: string) => void;

  // Column operations
  addColumn: (tableId: string, column: Column) => void;
  updateColumn: (tableId: string, columnId: string, data: Partial<Column>) => void;
  removeColumn: (tableId: string, columnId: string) => void;
  reorderColumns: (tableId: string, columnIds: string[]) => void;

  // Index operations
  addIndex: (tableId: string, index: Index) => void;
  removeIndex: (tableId: string, indexId: string) => void;

  // Schema management (local + cloud)
  saveSchema: (name: string, description?: string) => Promise<void>;
  loadSchema: (schemaId: string) => Promise<void>;
  deleteSchema: (schemaId: string) => Promise<void>;
  duplicateCloudSchema: (schemaId: string) => Promise<void>;
  resetSchema: () => void;

  // Cloud sync
  saveToCloud: (name: string, description?: string, isPublic?: boolean) => Promise<void>;
  loadFromCloud: (schemaId: string) => Promise<void>;
  fetchCloudSchemas: (organizationId?: string) => Promise<void>;
  deleteFromCloud: (schemaId: string) => Promise<void>;

  // Database operations
  setCurrentDatabase: (databaseId?: string) => void;
  executeSQL: (sql: string) => Promise<void>;

  // Utility
  getTableById: (id: string) => TableNode | undefined;
  exportAsSQL: () => string;
  exportAsJSON: () => string;
  importFromJSON: (json: string) => void;

  // Undo/Redo operations
  undo: () => void;
  redo: () => void;
  pushHistory: () => void;
}

// Helper to load from localStorage
const loadFromStorage = (key: string, defaultValue: any) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Helper to save to localStorage
const saveToStorage = (key: string, value: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save to localStorage:`, error);
  }
};

const MAX_HISTORY = 50;

export const useSchemaDesignerStore = create<SchemaDesignerState>((set, get) => ({
  // Initial state
  nodes: loadFromStorage('moniva-schema-nodes', []),
  edges: loadFromStorage('moniva-schema-edges', []),
  currentDatabaseId: undefined,
  savedSchemas: loadFromStorage('moniva-saved-schemas', []),
  currentSchemaId: undefined,
  isExecuting: false,
  isSaving: false,
  isLoading: false,
  lastSaveTime: undefined,
  syncStatus: 'idle',
  syncError: undefined,
  history: [],
  historyIndex: -1,
  canUndo: false,
  canRedo: false,

  // Auto-save to cloud helper
  _autoSaveToCloud: async () => {
    const state = get();
    const isCloudSchema = state.currentSchemaId && !state.currentSchemaId.startsWith('schema-');

    if (isCloudSchema) {
      const currentSchema = state.savedSchemas.find((s) => s.id === state.currentSchemaId);
      if (currentSchema) {
        // Debounce auto-save
        if (state._autoSaveTimer) {
          clearTimeout(state._autoSaveTimer);
        }

        const timer = setTimeout(async () => {
          try {
            await api.updateSchema(state.currentSchemaId!, {
              nodes: state.nodes,
              edges: state.edges,
            });
            set({ lastSaveTime: new Date() });
          } catch (error) {
            console.error('Auto-save failed:', error);
          }
        }, 1000); // 1 second debounce

        set({ _autoSaveTimer: timer });
      }
    }
  },

  // Node operations
  setNodes: (nodes) => {
    set({ nodes, lastSaveTime: new Date() });
    saveToStorage('moniva-schema-nodes', nodes);
    get()._autoSaveToCloud();
  },

  setEdges: (edges) => {
    set({ edges, lastSaveTime: new Date() });
    saveToStorage('moniva-schema-edges', edges);
    get()._autoSaveToCloud();
  },

  onNodesChange: (changes) => {
    set((state) => {
      const newNodes = applyNodeChanges(changes, state.nodes) as TableNode[];
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
    get()._autoSaveToCloud();
  },

  onEdgesChange: (changes) => {
    set((state) => {
      const newEdges = applyEdgeChanges(changes, state.edges);
      saveToStorage('moniva-schema-edges', newEdges);
      return { edges: newEdges, lastSaveTime: new Date() };
    });
    get()._autoSaveToCloud();
  },

  onConnect: (connection) => {
    set((state) => {
      const newEdges = addEdge(
        {
          ...connection,
          type: 'smoothstep',
          animated: true,
        },
        state.edges
      );
      saveToStorage('moniva-schema-edges', newEdges);
      return { edges: newEdges, lastSaveTime: new Date() };
    });
  },

  // Table operations
  addTable: (table) => {
    get().pushHistory();
    set((state) => {
      // Smart positioning: arrange tables in a grid layout
      const GRID_SPACING_X = 450; // Horizontal spacing between tables
      const GRID_SPACING_Y = 350; // Vertical spacing between tables
      const COLUMNS = 3; // Number of columns in the grid

      const index = state.nodes.length;
      const row = Math.floor(index / COLUMNS);
      const col = index % COLUMNS;

      // Generate unique ID with timestamp and random component
      const uniqueId = `table-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      const newNode: TableNode = {
        id: uniqueId,
        type: 'tableNode',
        position: {
          x: 100 + col * GRID_SPACING_X,
          y: 100 + row * GRID_SPACING_Y
        },
        data: table,
      };
      const newNodes = [...state.nodes, newNode];
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
    get()._autoSaveToCloud();
  },

  updateTable: (id, data) => {
    set((state) => {
      const newNodes = state.nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              ...data,
            },
          };
        }
        return node;
      });
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
  },

  removeTable: (id) => {
    get().pushHistory();
    set((state) => {
      const newNodes = state.nodes.filter((node) => node.id !== id);
      const newEdges = state.edges.filter((edge) => edge.source !== id && edge.target !== id);
      saveToStorage('moniva-schema-nodes', newNodes);
      saveToStorage('moniva-schema-edges', newEdges);
      return { nodes: newNodes, edges: newEdges, lastSaveTime: new Date() };
    });
  },

  duplicateTable: (id) => {
    get().pushHistory();
    set((state) => {
      const table = state.nodes.find((node) => node.id === id);
      if (!table) return state;

      const newNode: TableNode = {
        ...table,
        id: `table-${Date.now()}`,
        position: {
          x: table.position.x + 50,
          y: table.position.y + 50,
        },
        data: {
          ...table.data,
          name: `${table.data.name}_copy`,
          columns: table.data.columns.map((col) => ({
            ...col,
            id: `column-${Date.now()}-${Math.random()}`,
          })),
        },
      };
      const newNodes = [...state.nodes, newNode];
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
  },

  // Column operations
  addColumn: (tableId, column) => {
    get().pushHistory();
    set((state) => {
      const newNodes = state.nodes.map((node) => {
        if (node.id === tableId) {
          return {
            ...node,
            data: {
              ...node.data,
              columns: [...node.data.columns, column],
            },
          };
        }
        return node;
      });
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
  },

  updateColumn: (tableId, columnId, data) => {
    set((state) => {
      const newNodes = state.nodes.map((node) => {
        if (node.id === tableId) {
          return {
            ...node,
            data: {
              ...node.data,
              columns: node.data.columns.map((column) => {
                if (column.id === columnId) {
                  return {
                    ...column,
                    ...data,
                  };
                }
                return column;
              }),
            },
          };
        }
        return node;
      });
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
  },

  removeColumn: (tableId, columnId) => {
    get().pushHistory();
    set((state) => {
      const newNodes = state.nodes.map((node) => {
        if (node.id === tableId) {
          return {
            ...node,
            data: {
              ...node.data,
              columns: node.data.columns.filter((column) => column.id !== columnId),
            },
          };
        }
        return node;
      });
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
  },

  reorderColumns: (tableId, columnIds) => {
    set((state) => {
      const newNodes = state.nodes.map((node) => {
        if (node.id === tableId) {
          const columnMap = new Map(node.data.columns.map((col) => [col.id, col]));
          const reorderedColumns = columnIds.map((id) => columnMap.get(id)!).filter(Boolean);
          return {
            ...node,
            data: {
              ...node.data,
              columns: reorderedColumns,
            },
          };
        }
        return node;
      });
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
  },

  // Index operations
  addIndex: (tableId, index) => {
    set((state) => {
      const newNodes = state.nodes.map((node) => {
        if (node.id === tableId) {
          return {
            ...node,
            data: {
              ...node.data,
              indexes: [...(node.data.indexes || []), index],
            },
          };
        }
        return node;
      });
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
  },

  removeIndex: (tableId, indexId) => {
    set((state) => {
      const newNodes = state.nodes.map((node) => {
        if (node.id === tableId) {
          return {
            ...node,
            data: {
              ...node.data,
              indexes: (node.data.indexes || []).filter((index) => index.id !== indexId),
            },
          };
        }
        return node;
      });
      saveToStorage('moniva-schema-nodes', newNodes);
      return { nodes: newNodes, lastSaveTime: new Date() };
    });
  },

  // Schema management (local)
  saveSchema: async (name, description) => {
    const state = get();
    const schema: SavedSchema = {
      id: state.currentSchemaId || `schema-${Date.now()}`,
      name,
      description,
      databaseId: state.currentDatabaseId,
      nodes: state.nodes,
      edges: state.edges,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set((state) => {
      const existing = state.savedSchemas.find((s) => s.id === schema.id);
      const newSchemas = existing
        ? state.savedSchemas.map((s) => (s.id === schema.id ? schema : s))
        : [...state.savedSchemas, schema];

      saveToStorage('moniva-saved-schemas', newSchemas);
      return {
        savedSchemas: newSchemas,
        currentSchemaId: schema.id,
        lastSaveTime: new Date(),
      };
    });
  },

  loadSchema: async (schemaId) => {
    const state = get();
    const schema = state.savedSchemas.find((s) => s.id === schemaId);
    if (schema) {
      set({
        nodes: schema.nodes,
        edges: schema.edges,
        currentSchemaId: schema.id,
        currentDatabaseId: schema.databaseId,
      });
      saveToStorage('moniva-schema-nodes', schema.nodes);
      saveToStorage('moniva-schema-edges', schema.edges);
    }
  },

  deleteSchema: async (schemaId) => {
    set((state) => {
      const newSchemas = state.savedSchemas.filter((s) => s.id !== schemaId);
      saveToStorage('moniva-saved-schemas', newSchemas);
      return {
        savedSchemas: newSchemas,
        currentSchemaId: state.currentSchemaId === schemaId ? undefined : state.currentSchemaId,
      };
    });
  },

  duplicateCloudSchema: async (schemaId) => {
    try {
      set({ isLoading: true, syncStatus: 'loading', syncError: undefined });
      const response = await api.duplicateSchema(schemaId);

      if (response.success) {
        // Fetch updated list
        await get().fetchCloudSchemas();
      }
    } catch (error: any) {
      console.error('Failed to duplicate schema:', error);
      set({ syncStatus: 'error', syncError: error.message || 'Failed to duplicate schema' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Cloud sync methods
  saveToCloud: async (name, description, isPublic = false) => {
    const state = get();

    try {
      set({ isSaving: true, syncStatus: 'saving', syncError: undefined });

      if (state.currentSchemaId && state.currentSchemaId.startsWith('schema-')) {
        // Local schema - create new cloud schema
        const response = await api.createSchema({
          name,
          description,
          nodes: state.nodes,
          edges: state.edges,
          databaseId: state.currentDatabaseId,
          isPublic,
        });

        if (response.success) {
          set({
            currentSchemaId: response.schema.id,
            lastSaveTime: new Date(),
            syncStatus: 'idle',
          });
        }
      } else if (state.currentSchemaId) {
        // Existing cloud schema - update
        const response = await api.updateSchema(state.currentSchemaId, {
          name,
          description,
          nodes: state.nodes,
          edges: state.edges,
          databaseId: state.currentDatabaseId,
          isPublic,
        });

        if (response.success) {
          set({
            lastSaveTime: new Date(),
            syncStatus: 'idle',
          });
        }
      } else {
        // No current schema - create new
        const response = await api.createSchema({
          name,
          description,
          nodes: state.nodes,
          edges: state.edges,
          databaseId: state.currentDatabaseId,
          isPublic,
        });

        if (response.success) {
          set({
            currentSchemaId: response.schema.id,
            lastSaveTime: new Date(),
            syncStatus: 'idle',
          });
        }
      }

      // Refresh cloud schemas list
      await get().fetchCloudSchemas();
    } catch (error: any) {
      console.error('Failed to save to cloud:', error);
      set({ syncStatus: 'error', syncError: error.message || 'Failed to save to cloud' });
      throw error;
    } finally {
      set({ isSaving: false });
    }
  },

  loadFromCloud: async (schemaId) => {
    try {
      set({ isLoading: true, syncStatus: 'loading', syncError: undefined });

      const response = await api.getSchema(schemaId);

      if (response.success) {
        const schema = response.schema;
        set({
          nodes: schema.nodes,
          edges: schema.edges,
          currentSchemaId: schema.id,
          currentDatabaseId: schema.databaseId,
          syncStatus: 'idle',
        });

        // Save to localStorage for offline access
        saveToStorage('moniva-schema-nodes', schema.nodes);
        saveToStorage('moniva-schema-edges', schema.edges);
      }
    } catch (error: any) {
      console.error('Failed to load from cloud:', error);
      set({ syncStatus: 'error', syncError: error.message || 'Failed to load from cloud' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  fetchCloudSchemas: async (organizationId?: string) => {
    try {
      set({ isLoading: true, syncStatus: 'loading', syncError: undefined });

      const response = await api.getSchemas(organizationId);

      if (response.success) {
        // Merge with local schemas
        const cloudSchemas = response.schemas.map((s: any) => ({
          ...s,
          createdAt: new Date(s.createdAt),
          updatedAt: new Date(s.updatedAt),
        }));

        const localSchemas = get().savedSchemas.filter((s) => s.id.startsWith('schema-'));
        const allSchemas = [...cloudSchemas, ...localSchemas];

        set({
          savedSchemas: allSchemas,
          syncStatus: 'idle',
        });

        saveToStorage('moniva-saved-schemas', allSchemas);
      }
    } catch (error: any) {
      console.error('Failed to fetch cloud schemas:', error);
      set({ syncStatus: 'error', syncError: error.message || 'Failed to fetch schemas' });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteFromCloud: async (schemaId) => {
    try {
      set({ isLoading: true, syncStatus: 'loading', syncError: undefined });

      const response = await api.deleteSchema(schemaId);

      if (response.success) {
        set((state) => {
          const newSchemas = state.savedSchemas.filter((s) => s.id !== schemaId);
          saveToStorage('moniva-saved-schemas', newSchemas);
          return {
            savedSchemas: newSchemas,
            currentSchemaId: state.currentSchemaId === schemaId ? undefined : state.currentSchemaId,
            syncStatus: 'idle',
          };
        });
      }
    } catch (error: any) {
      console.error('Failed to delete from cloud:', error);
      set({ syncStatus: 'error', syncError: error.message || 'Failed to delete from cloud' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  resetSchema: () => {
    if (window.confirm('Are you sure you want to reset the entire schema? This cannot be undone.')) {
      set({ nodes: [], edges: [], currentSchemaId: undefined });
      saveToStorage('moniva-schema-nodes', []);
      saveToStorage('moniva-schema-edges', []);
    }
  },

  // Database operations
  setCurrentDatabase: (databaseId) => {
    set({ currentDatabaseId: databaseId });
  },

  executeSQL: async (sql) => {
    set({ isExecuting: true });
    try {
      // TODO: Implement actual SQL execution via API
      console.log('Executing SQL:', sql);
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } finally {
      set({ isExecuting: false });
    }
  },

  // Utility functions
  getTableById: (id) => {
    return get().nodes.find((node) => node.id === id);
  },

  exportAsSQL: () => {
    // TODO: Implement SQL generation
    return '-- SQL export not implemented yet';
  },

  exportAsJSON: () => {
    const state = get();
    return JSON.stringify(
      {
        nodes: state.nodes,
        edges: state.edges,
      },
      null,
      2
    );
  },

  importFromJSON: (json) => {
    try {
      const data = JSON.parse(json);
      if (data.nodes && data.edges) {
        get().pushHistory();
        set({ nodes: data.nodes, edges: data.edges });
        saveToStorage('moniva-schema-nodes', data.nodes);
        saveToStorage('moniva-schema-edges', data.edges);
      }
    } catch (error) {
      console.error('Failed to import JSON:', error);
    }
  },

  // Undo/Redo operations
  pushHistory: () => {
    const state = get();
    const newHistory = state.history.slice(0, state.historyIndex + 1);
    newHistory.push({
      nodes: JSON.parse(JSON.stringify(state.nodes)),
      edges: JSON.parse(JSON.stringify(state.edges)),
    });

    // Limit history size
    if (newHistory.length > MAX_HISTORY) {
      newHistory.shift();
    }

    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
      canUndo: newHistory.length > 0,
      canRedo: false,
    });
  },

  undo: () => {
    const state = get();
    if (state.historyIndex > 0) {
      const newIndex = state.historyIndex - 1;
      const historyState = state.history[newIndex];

      set({
        nodes: JSON.parse(JSON.stringify(historyState.nodes)),
        edges: JSON.parse(JSON.stringify(historyState.edges)),
        historyIndex: newIndex,
        canUndo: newIndex > 0,
        canRedo: true,
      });

      saveToStorage('moniva-schema-nodes', historyState.nodes);
      saveToStorage('moniva-schema-edges', historyState.edges);
    }
  },

  redo: () => {
    const state = get();
    if (state.historyIndex < state.history.length - 1) {
      const newIndex = state.historyIndex + 1;
      const historyState = state.history[newIndex];

      set({
        nodes: JSON.parse(JSON.stringify(historyState.nodes)),
        edges: JSON.parse(JSON.stringify(historyState.edges)),
        historyIndex: newIndex,
        canUndo: true,
        canRedo: newIndex < state.history.length - 1,
      });

      saveToStorage('moniva-schema-nodes', historyState.nodes);
      saveToStorage('moniva-schema-edges', historyState.edges);
    }
  },
}));
